import { createState } from "@/global/store/zustand_store";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { Box, Stack, Typography } from "@mui/material";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import {
  SeatSelectionMode,
  useAutoSeatSelectionState,
} from "./useAutoSeatSelectionState";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";

export interface AutoSeatSelectionAlertModalResponse {
  action: SeatSelectionMode;
}

export const useModalState = createState({
  isOpen: false,
  onFinish: (a: AutoSeatSelectionAlertModalResponse) => {},
});

export const useAutoSeatSelectionAlertModal = () => {
  const autoSeatSelectionState = useAutoSeatSelectionState();

  const { isOpen, onFinish, set, reset } = useModalState();

  const execute = () => {
    return new Promise<AutoSeatSelectionAlertModalResponse>((resolve) => {
      if (autoSeatSelectionState.mode == "changeInitialSelection") {
        return resolve({ action: "autoSelect" });
      } else if (autoSeatSelectionState.mode == "manualSelect") {
        return resolve({ action: "manualSelect" });
      } else if (autoSeatSelectionState.mode == "autoSelect") {
        return resolve({ action: "autoSelect" });
      } else set({ isOpen: true, onFinish: resolve });
    });
  };

  return {
    isOpen,
    onFinish,
    set,
    reset,
    execute,
  };
};

export function AutoSeatSelectionAlertModal() {
  const teamConfig = useTeamConfig();
  const { md: mdDown } = useCssMediaQuery("down");
  const modalState = useAutoSeatSelectionAlertModal();
  const autoSeatSelectionState = useAutoSeatSelectionState();

  const handleClose = () => {
    modalState.set({ isOpen: false });
  };

  const onClickAutoSeatSelection = () => {
    autoSeatSelectionState.set({ mode: "autoSelect" });
    modalState.onFinish({ action: "autoSelect" });
    handleClose();
  };

  const onClickManualSeatSelection = () => {
    autoSeatSelectionState.set({ mode: "manualSelect" });
    modalState.onFinish({ action: "manualSelect" });
    handleClose();
  };

  const onClickChangeInitialSelection = () => {
    autoSeatSelectionState.set({ mode: "changeInitialSelection" });
    modalState.onFinish({ action: "changeInitialSelection" });
    handleClose();
  };

  return (
    <>
      <NotificationModal
        isOpen={modalState.isOpen}
        hideTitleIcon
        hideCloseIcon={true}
        title="Automatic Seat Selection"
        description={
          <Box>
            <Box>
              {teamConfig?.purchaseSeatConfigs?.allowManualCartInSimilar ? (
                <>
                  {teamConfig?.languages?.autoSeatSelectionAlertModal
                    ?.description?.manualSelectionAllowed || (
                    <Typography variant="body1">
                      Would you like to automatically select similar seats for
                      your remaining events or choose seats manually?
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  {teamConfig?.languages?.autoSeatSelectionAlertModal
                    ?.description?.manualSelectionNotAllowed || (
                    <Typography variant="body1">
                      For multi-event packages, your initial seat choice
                      establishes your seating preference for all events.
                      <br />
                      <br />
                      We’ll assign{" "}
                      <b>the same seat for each event whenever possible</b>. If
                      that seat isn’t available, you’ll be assigned a comparable
                      seat.
                      <br />
                      <br />
                      This process happens by default, and browsing individual
                      seats for each event is not available.
                      <br />
                      <br />
                      If you’d like to change your seating preference, simply
                      click <b>Change Seat</b> before continuing.
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        }
        renderFooter={() => {
          return (
            <Stack
              p={2}
              direction={mdDown ? "column" : "row"}
              justifyContent="flex-end"
              spacing={2}
            >
              {teamConfig?.purchaseSeatConfigs?.allowManualCartInSimilar ? (
                <ModalActionButton
                  variant="outlined"
                  onClick={onClickManualSeatSelection}
                >
                  Select Seats Manually
                </ModalActionButton>
              ) : (
                <ModalActionButton
                  variant="outlined"
                  onClick={onClickChangeInitialSelection}
                >
                  {teamConfig?.languages?.autoSeatSelectionAlertModal
                    ?.changeSeatButtonText || "Change Seat"}
                </ModalActionButton>
              )}

              <ModalActionButton
                variant="primary"
                onClick={onClickAutoSeatSelection}
              >
                Automatically Select Similar Seats
              </ModalActionButton>
            </Stack>
          );
        }}
      />
    </>
  );
}
